import type { ReactNode } from 'react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  useIsMobile,
} from '@design-system';

import { formatCurrency } from '@shared';

import { type CcrcTypeAProductMetrics } from '../lib/ccrc-type-a-metrics';
import { type CcrcTypeAPricingBreakdown } from '../lib/ccrc-type-a-pricing';

interface DetailRow {
  label: string;
  value: ReactNode;
}

interface DetailSection {
  title: string;
  rows: DetailRow[];
}

function buildSections(
  product: CcrcTypeAProduct,
  pricing: CcrcTypeAPricingBreakdown,
  metrics: CcrcTypeAProductMetrics,
): DetailSection[] {
  const increasePercent = +(product.annualIncreaseRate * 100).toFixed(2);

  return [
    {
      title: 'Membership Pricing',
      rows: [
        { label: 'One-Time Entrance Fee', value: formatCurrency(pricing.totalEntranceFee) },
        {
          label: 'Monthly Maintenance Fee',
          value: `${formatCurrency(pricing.totalMonthlyFee)}/mo`,
        },
        { label: 'Annual Fee Increase', value: `${increasePercent}%` },
      ],
    },
    {
      title: 'LTC Coverage',
      rows: [
        { label: 'Covered Care', value: 'All levels of long-term care' },
        { label: 'Benefit Limit', value: 'No maximum' },
        { label: 'Projected Coverage', value: formatCurrency(metrics.projectedCoverage) },
      ],
    },
    {
      title: 'Plan Value',
      rows: [
        { label: 'Total Plan Cost', value: formatCurrency(metrics.cost) },
        { label: 'Total ROI', value: `${(metrics.totalRoi * 100).toFixed(2)}%` },
      ],
    },
  ];
}

function DetailsContent({ sections }: { sections: DetailSection[] }) {
  return (
    <div className="space-y-6">
      {sections.map(section => (
        <div key={section.title}>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-950">
            {section.title}
          </h4>
          <Separator className="mb-2" />
          <div className="space-y-2">
            {section.rows.map(row => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-4"
              >
                <span className="text-foreground text-xs font-medium">{row.label}</span>
                <span className="text-right text-xs font-semibold text-gray-950">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface CcrcTypeAProductDetailsHoverCardProps {
  product: CcrcTypeAProduct;
  pricing: CcrcTypeAPricingBreakdown;
  metrics: CcrcTypeAProductMetrics;
  children: ReactNode;
}

export function CcrcTypeAProductDetailsHoverCard({
  product,
  pricing,
  metrics,
  children,
}: CcrcTypeAProductDetailsHoverCardProps) {
  const isMobile = useIsMobile();
  const content = <DetailsContent sections={buildSections(product, pricing, metrics)} />;

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          className="w-72"
          align="start"
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="w-80"
        align="start"
      >
        {content}
      </HoverCardContent>
    </HoverCard>
  );
}
